import { uploadDsdEditorFile } from "@/apis/dsd-editor/route";
import { logUserAction } from "@/apis/logs/route";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import queries from "@/queries";
import { existsModal, popModal, pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { openPendingTab } from "@/utils/pending-tab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

const DSD_REVIEW_FAILED_MODAL_ID = "dsd-review-failed-modal";

const DsdReviewFailedModal = () => (
  <ModalWrapper className="flex w-[512px] max-w-lg flex-col items-end gap-4 rounded-[10px] border border-line-normal bg-background-base p-6 shadow-lg">
    <div className="flex flex-col gap-2 self-stretch">
      <h2 className="headline-1-semibold text-label-normal">
        DSD 리뷰에 실패했습니다.
      </h2>
      <p className="body-1-normal-medium leading-relaxed text-label-alternative break-keep">
        반복하여 실패할 경우 문의해주세요.
      </p>
    </div>
    <div className="flex items-center justify-end gap-2 self-stretch">
      <Button
        variant="solid"
        color="primary"
        size="base"
        className="min-w-[80px]"
        onClick={() => popModal()}
      >
        확인
      </Button>
    </div>
  </ModalWrapper>
);

export const openDsdReviewFailedModal = () => {
  if (existsModal(DSD_REVIEW_FAILED_MODAL_ID)) return;

  pushModal(DsdReviewFailedModal, { id: DSD_REVIEW_FAILED_MODAL_ID });
};

export const isDsdReviewFailedModalOpen = () =>
  existsModal(DSD_REVIEW_FAILED_MODAL_ID);

const buildDsdReviewViewUrl = (documentId: string) => {
  const url = new URL("/dsd-editor/view", window.location.origin);
  url.searchParams.set("document_id", documentId);
  url.searchParams.set("launched_from_editor", "true");
  return url.toString();
};

/** 리뷰 화면은 업로드 화면을 런처로 남기고 새 탭에서 연다. */
export const openDsdReviewView = (documentId: string) => {
  window.open(buildDsdReviewViewUrl(documentId), "_blank", "noopener");
};

const getUploadedDocumentId = (data: unknown) => {
  if (!data || typeof data !== "object") return null;

  const record = data as Record<string, unknown>;
  const documentId =
    record.report_id ??
    record.reportId ??
    record.document_id ??
    record.documentId;

  return typeof documentId === "string" && documentId ? documentId : null;
};

const closePendingWindow = (viewWindow: Window | null) => {
  if (!viewWindow || viewWindow.closed) return;

  viewWindow.close();
};

/**
 * DSD 파일을 올려 리뷰 화면을 새 탭으로 연다. 실패하면 리뷰 실패 모달을 띄운다.
 * DSD 리뷰 화면과 홈 STEP2 패널이 함께 쓴다.
 *
 * WHY: 업로드 응답을 기다린 뒤 window.open 하면 팝업 차단에 걸리므로, 클릭 시점에 로딩 탭을 먼저 열고
 * 응답이 오면 그 탭의 주소를 바꾼다.
 */
export const useStartDsdReview = () => {
  const queryClient = useQueryClient();
  const pendingViewWindowRef = useRef<Window | null>(null);

  const mutation = useMutation({
    mutationFn: (file: File) => uploadDsdEditorFile(file),
    onSuccess: (data, uploadedFile) => {
      // 실제 키는 ["server","dsdEditorReports","dsd-editor-reports",limit,offset] 라
      // 손으로 쓴 prefix 는 하나도 못 맞춘다. _def 로 페이지 전체를 무효화한다.
      void queryClient.invalidateQueries({
        queryKey: queries.dsdEditorReports._def,
      });
      const documentId = getUploadedDocumentId(data);
      const pendingViewWindow = pendingViewWindowRef.current;
      pendingViewWindowRef.current = null;

      if (!documentId) {
        closePendingWindow(pendingViewWindow);
        openDsdReviewFailedModal();
        return;
      }

      logUserAction("DSD 리뷰 시작", [
        { filename: uploadedFile.name, dsdFileId: documentId },
      ]);

      if (pendingViewWindow && !pendingViewWindow.closed) {
        pendingViewWindow.location.replace(buildDsdReviewViewUrl(documentId));
        return;
      }

      openDsdReviewView(documentId);
    },
    onError: () => {
      closePendingWindow(pendingViewWindowRef.current);
      pendingViewWindowRef.current = null;
      openDsdReviewFailedModal();
    },
  });

  const start = (file: File) => {
    if (mutation.isPending) return;

    pendingViewWindowRef.current = openPendingTab({
      title: "DSD 리뷰 화면 준비 중...",
      message: "DSD 리뷰 화면을 준비하고 있습니다...",
      variant: "dsd-review",
    });
    mutation.mutate(file);
  };

  return {
    start,
    isPending: mutation.isPending,
    isError: mutation.isError,
    reset: mutation.reset,
  };
};
